import { Box, Image, SxProps, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import {
  TFunction,
  renderCurrencyString,
  useTranslation,
} from "@outschool/localization";
import { SupportRoutes } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import { Screen } from "@outschool/ui-utils";
import React from "react";

import giftImage from "../../images/ReferralsProgram_2023.png";
import exhausiveCheck from "../utils/exhaustiveCheck";
import { CopyToClipboard } from "./CopyToClipboard";
import DesktopReferralShareButton from "./DesktopReferralShareButton";
import { MobileTabletReferralShareButton } from "./MobileTabletReferralShareButton";
import { uiContextIds } from "./Referrals";
import { ModalContext, addShareUtmCampaign } from "./utils";

export type ShareContentType = {
  shareLink: string;
  modalContext: ModalContext;
  trackingMetaData?: {
    [key: string]: any;
  };
  activity?: { uid: string };
  facebookAppId?: string;
};

function getHeading({
  isLoggedIn,
  modalContext,
  recipientCredit: creditCashAmount,
  t,
}: {
  isLoggedIn: boolean;
  modalContext: ModalContext;
  recipientCredit: string;
  t: TFunction;
}) {
  const loggedOutShareString = (() => {
    switch (modalContext) {
      case ModalContext.UnenrolledClass:
        return t`Share this class with a friend!`;
      case ModalContext.EnrolledClass:
        return t`Invite your friends to class!`;
      case ModalContext.SharedEducatorWithFriend:
        return t`Share this teacher with a friend!`;
      case ModalContext.SharedEducatorProfile:
        return t`Share your profile!`;
      case ModalContext.FavoritesList:
        return t`Share your favorites list with a friend!`;
      case ModalContext.GenericReferral:
        return t`Share Outschool with a friend!`;
      default:
        throw exhausiveCheck(modalContext);
    }
  })();

  const loggedInShareString = (() => {
    switch (modalContext) {
      case ModalContext.UnenrolledClass:
        return t(
          "Share this class with a friend — new users will get {{creditCashAmount}} off!",
          { creditCashAmount }
        );
      case ModalContext.EnrolledClass:
        return t(
          "Invite your friends to class — new users will get {{creditCashAmount}} off!",
          { creditCashAmount }
        );
      case ModalContext.SharedEducatorWithFriend:
        return t(
          "Share this teacher with a friend — new users will get {{creditCashAmount}} off!",
          { creditCashAmount }
        );
      case ModalContext.SharedEducatorProfile:
        return t(
          "Share your profile — new users will get {{creditCashAmount}} off!",
          {
            creditCashAmount,
          }
        );
      case ModalContext.FavoritesList:
        return t(
          "Share Outschool with a friend — new users will get {{creditCashAmount}} off!",
          { creditCashAmount }
        );
      case ModalContext.GenericReferral:
        return t(
          "Share Outschool with a friend — new users will get {{creditCashAmount}} off!",
          { creditCashAmount }
        );
      default:
        throw exhausiveCheck(modalContext);
    }
  })();

  return isLoggedIn ? loggedInShareString : loggedOutShareString;
}

export const ShareContent = ({
  shareLink,
  modalContext,
  trackingMetaData = {},
  activity,
  facebookAppId,
}: ShareContentType) => {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\ActivityShareDialogContent"
  );

  const recipientCredit = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
  });

  const { isLoggedIn, currentUser: user, currentUserIsLoading } = useSession();

  if (currentUserIsLoading) {
    return <Box>{t("Loading...")}</Box>;
  }

  const heading = getHeading({ isLoggedIn, modalContext, recipientCredit, t });

  return (
    <Box
      flex
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1em",
        textAlign: "center",
      }}
    >
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: "0.5em",
          justifyContent: "flex-start",
        }}
      >
        <Box>
          <Box
            sx={{
              textAlign: "center",
              margin: "10px",
            }}
          >
            <Image
              src={typeof giftImage === "string" ? giftImage : giftImage.src}
              height="120"
              width="120"
            />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Ginto Normal',sans-serif",
            }}
          >
            {heading}
          </Typography>
          {isLoggedIn && (
            <>
              <Box sx={{ margin: 10 }}>
                {t(
                  "You’ll also get {{creditCashAmount}} when they sign up with your link and take their first class!",
                  { creditCashAmount: recipientCredit }
                )}
              </Box>
              <ExternalLink
                url={SupportRoutes.supportParentReferralProgramUrl()}
              >
                {t("See parent referral terms.")}
              </ExternalLink>
            </>
          )}
        </Box>
      </Box>

      <Box
        flex
        sx={{
          flexFlow: "column",
          justifyContent: "flex-start",
        }}
      >
        <ShareLinkArea
          shareLink={shareLink}
          trackingUniqueId={activity?.uid || user?.uid || Date.now().toString()}
          trackingMetadata={trackingMetaData}
          uiContextId={uiContextIds.ClassPage}
          modalContext={modalContext}
          recipientCredit={recipientCredit}
          label={t("Share your referral link")}
          copyButtonLabel={t("Copy Link")}
          facebookAppId={facebookAppId}
        />
      </Box>
    </Box>
  );
};

/**
 * Compound component for showing a copy-to-clipboard input and button along
 * with platform-specific share button(s) below that.
 */
export const ShareLinkArea = ({
  shareLink,
  label,
  copyButtonLabel,
  uiContextId,
  trackingName = "share_link_copy_button",
  trackingUniqueId,
  modalContext,
  recipientCredit,
  trackingMetadata = {},
  sx = {},
  facebookAppId,
}: ShareLinkAreaProps) => {
  const { isLoggedIn, currentUser } = useSession();
  const isMobile = Screen.useIsMobile();
  const isTablet = Screen.useIsTablet();
  const doesWebShareApiExist = !!navigator?.share;
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\ActivityShareDialogContent"
  );

  return (
    <Box
      flex
      sx={[
        {
          flexFlow: "column",
          gap: "1em",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {(isMobile || isTablet) && doesWebShareApiExist ? (
        <>
          <MobileTabletReferralShareButton
            trackingSharedProperties={{ uiContextId }}
            isLoggedIn={isLoggedIn}
            usid={currentUser?.slug_id ?? ""}
            modalContext={modalContext}
            recipientCredit={recipientCredit}
            value={addShareUtmCampaign(
              shareLink,
              modalContext,
              "web_share_api"
            )}
          />
          <CopyToClipboard
            label={label}
            uiContextId={uiContextId}
            inputValue={addShareUtmCampaign(shareLink, modalContext, "link")}
            hideInput={true}
            copyButtonLabel={copyButtonLabel}
            trackingName={trackingName}
            trackingUniqueId={trackingUniqueId}
            trackingMetaData={trackingMetadata}
          />
        </>
      ) : (
        <>
          <CopyToClipboard
            label={label}
            uiContextId={uiContextId}
            inputValue={addShareUtmCampaign(shareLink, modalContext, "link")}
            hideInput={false}
            // eslint-disable-next-line i18next/no-literal-string
            buttonVariant="contained"
            copyButtonLabel={t`Copy`}
            trackingName={trackingName}
            trackingUniqueId={trackingUniqueId}
            trackingMetaData={trackingMetadata}
          />
          <DesktopReferralShareButton
            sx={{ marginTop: "medium" }}
            isLoggedIn={isLoggedIn}
            usid={currentUser?.slug_id ?? ""}
            trackingSharedProperties={{ uiContextId }}
            shareLink={shareLink}
            modalContext={modalContext}
            recipientCredit={recipientCredit}
            facebookAppId={facebookAppId}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </>
      )}
    </Box>
  );
};

export const ShareLinkAreaParentProfile = ({
  shareLink,
  label,
  uiContextId,
  trackingName = "share_link_copy_button",
  trackingUniqueId,
  modalContext,
  recipientCredit,
  trackingMetadata = {},
  sx = {},
  facebookAppId,
}: ShareLinkAreaProfileProps) => {
  const { isLoggedIn, currentUser } = useSession();
  const isMobile = Screen.useIsMobile();
  const isTablet = Screen.useIsTablet();
  const doesWebShareApiExist = !!navigator?.share;
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\ActivityShareDialogContent"
  );

  return (
    <Box
      flex
      sx={[
        {
          flexFlow: "column",
          gap: "1em",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {(isMobile || isTablet) && doesWebShareApiExist ? (
        <>
          <MobileTabletReferralShareButton
            trackingSharedProperties={{ uiContextId }}
            isLoggedIn={isLoggedIn}
            usid={currentUser?.slug_id ?? ""}
            modalContext={modalContext}
            recipientCredit={recipientCredit}
            value={addShareUtmCampaign(
              shareLink,
              modalContext,
              "web_share_api"
            )}
          />
        </>
      ) : (
        <>
          <CopyToClipboard
            label={label}
            uiContextId={uiContextId}
            inputValue={addShareUtmCampaign(shareLink, modalContext, "link")}
            hideInput={false}
            // eslint-disable-next-line i18next/no-literal-string
            buttonVariant="contained"
            copyButtonLabel={t`Copy`}
            trackingName={trackingName}
            trackingUniqueId={trackingUniqueId}
            trackingMetaData={trackingMetadata}
          />
          <DesktopReferralShareButton
            sx={{ marginTop: "medium" }}
            isLoggedIn={isLoggedIn}
            usid={currentUser?.slug_id ?? ""}
            trackingSharedProperties={{ uiContextId }}
            shareLink={shareLink}
            modalContext={modalContext}
            recipientCredit={recipientCredit}
            facebookAppId={facebookAppId}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </>
      )}
    </Box>
  );
};

type ShareLinkAreaProps = Pick<
  ShareContentType,
  "shareLink" | "modalContext"
> & {
  label: string;
  copyButtonLabel: string;
  recipientCredit: string;
  uiContextId: string;
  trackingName?: string;
  trackingUniqueId?: string;
  trackingMetadata?: ShareContentType["trackingMetaData"];
  sx?: SxProps;
  facebookAppId?: string;
};

type ShareLinkAreaProfileProps = ShareLinkAreaProps & {
  updateConsent: (value: boolean) => Promise<void>;
};
